<template>
  <div v-if="showAlertForSmallScreen" class="alert-small-screen">
    <p>This site is only supported on larger screens. Please use a desktop or maximize your browser window.</p>
  </div>
  <div class="faq-container">
    <div class="faq-title">Frequently Asked Questions</div>
    <div class="faq-question">1. What human reference genome build are the variant coordinates based on?</div>
    <div class="faq-answer">
      <ul>
        <li>All variant coordinates in AGES are based on the <mark>GRCh37/hg19</mark> reference genome.</li>
      </ul>
    </div>

    <div class="faq-question">2. What are the <mark>WHG</mark>, <mark>EEF</mark>, and <mark>Steppe</mark> populations shown in the allele frequency trajectory plots?</div>
    <div class="faq-answer">
      <ul>
        <li>These populations represent the three major ancestral sources of present-day Europeans. Their allele frequencies are shown to illustrate how ancient migrations and population mixing shaped genetic variation over time. Although our analysis accounts for population structure and admixture, these allele frequency data are not directly used in the model and are provided only for context.
        <ul>
          <li>WHG (Western Hunter-Gatherers): Hunter-gatherer groups who lived across Europe after the Last Ice Age (~15,000–7,000 years ago).</li>
          <li>EEF (Early European Farmers): Early farming communities who migrated from Anatolia into Europe around 8,000–6,000 years ago, introducing agriculture and a settled lifestyle.</li>
          <li>Steppe (Yamnaya-related pastoralists): Herding populations from the Pontic–Caspian Steppe who moved into Europe about 5,000–4,000 years ago, contributing substantially to the ancestry of northern and eastern Europeans.</li>
        </ul>
        <br>
        For more information, see [1, 2].
        </li>

      </ul>
    </div>

    <div class="faq-question">3. How does AGES estimate selection coefficients from ancient DNA data?</div>
    <div class="faq-answer">
      <ul>
        <li>AGES uses a generalized linear mixed model (GLMM) to estimate selection coefficients by testing whether allele frequencies show a consistent directional trend through time beyond what is expected from population structure and genetic drift. The model fits a time-varying component to the logit of allele frequency and incorporates a genetic relationship matrix (GRM) to correct for shared ancestry and admixture. This framework allows AGES to distinguish true selection from allele frequency changes driven by population mixture or structure [3].</li>
      </ul>
    </div>

    <div class="faq-question">4. Does AGES assume constant selection through time?</div>
    <div class="faq-answer">
      <ul>
        <li>The core model assumes a constant selection coefficient across the analyzed time period, providing a time-averaged estimate of the strength and direction of selection. This simplifying assumption reduces the number of parameters and increases statistical power, and has proven effective in detecting hundreds of genome-wide significant signals of selection [3].
 However, some loci exhibit changing selection pressures over time (for example, at HLA-DRB1, TYK2, and HFE), which violates the assumption of consistent selection and cannot be captured by this model.</li>
      </ul>
    </div>

    <div class="faq-question">5. Where can selection coefficients and summary statistics for each variant be accessed?</div>
    <div class="faq-answer">
      <ul>
        <li>Summary statistics for 9.7 million variants, including selection coefficients, standard errors, and posterior probabilities of selection, are available <a style="color: #A51C30; text-decoration: none;" target="_blank" href="https://doi.org/10.7910/DVN/7RVV9N">here</a>.
</li>
      </ul>
    </div>

    <div class="faq-title">References</div>
    <ol class="reference">
      <li id="ref1">
        Haak, W., Lazaridis, I., Patterson, N. et al. Massive migration from the steppe was a source for Indo-European languages in Europe. Nature 522, 207–211 (2015). <a style="color: #A51C30; text-decoration: none;" target="_blank" href="https://doi.org/10.1038/nature14317">doi.org/10.1038/nature14317</a>
      </li>
      <li id="ref2">
        Lazaridis, I., Patterson, N., Mittnik, A. et al. Ancient human genomes suggest three ancestral populations for present-day Europeans. Nature 513, 409–413 (2014). <a style="color: #A51C30; text-decoration: none;" target="_blank" href="https://doi.org/10.1038/nature13673">doi.org/10.1038/nature13673</a>
      </li>
      <li id="ref3">
        Akbari, A. et al. Pervasive findings of directional selection realize the promise of ancient DNA to elucidate human adaptation. 2024.09.14.613021 Preprint at <a style="color: #A51C30; text-decoration: none;" target="_blank" href="https://doi.org/10.1101/2024.09.14.613021">doi.org/10.1101/2024.09.14.613021</a>(2024).
      </li>
    </ol>
  </div>

</template>

<script>
import { smallScreenMixin } from '@/mixins/smallScreenMixin';

export default {
  name: "FAQ",
  mixins: [smallScreenMixin],
  methods: {
    scroll(_id, top = 90) {
      const manhattanDiv = document.getElementById(_id);
      if (manhattanDiv) {
        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });

        manhattanDiv.classList.add("highlight");

        setTimeout(() => {
          manhattanDiv.classList.remove("highlight");
        }, 3000);
      }
    }
  },
  mounted() {
    const hashParts = window.location.hash.split("#");
    if (hashParts.length > 1) {
      let part = hashParts[hashParts.length - 1]
      if (part == 'manhattan') {
        this.scroll(part, 90)
      } else if (part == 'pheno_more') {
        this.scroll(part, 800)
      } else if (part == 'info-selection') {
        this.scroll(part, 1300)
      } else if (part == 'info-gwas') {
        this.scroll(part, 1300)
      } else if (part == 'info-selection-gwas') {
        this.scroll(part, 1500)
      }

    }
  }
};
</script>

<style>
.faq-container {
  width: 945px;
  margin: 100px auto;
  text-align: left;
  scroll-behavior: smooth;
  overflow-y: auto;
}

.faq-title {
  font-size: 24px;
  margin-top: 30px;
  scroll-behavior: smooth;
  font-weight: bold;
  margin-bottom: 20px;
}

ol li {
  margin-left: 19px;
  margin-top: 30px;
  text-align: justify;
}

ul li {
  margin-left: 20px;
}


.faq-question {
  font-weight: bold;
  scroll-behavior: smooth;
  margin-top: 20px;
  text-align: justify;
}

.reference {
  line-height: 23px;
}

.faq-answer {
  margin-top: 10px;
  font-weight: normal;
  overflow-y: auto;
  scroll-behavior: smooth;
  line-height: 23px;
}

.faq-answer ul li {
  text-align: justify;
  line-height: 25px;
  ;
}

.alert-small-screen {
  font-size: 1.3rem;
  width: 90vw;
  margin: 0 auto;
  text-align: center;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

mark {
  background-color: #e3e3e0;
  padding: 0 2px;
  border-radius: 4px;
}

.highlight {
  animation: highlightFade 8s ease;
  background-color: #a51c312a;
  border-radius: 4px;
  /* Or any color for highlighting */
}

@keyframes highlightFade {
  0% {
    background-color: #a51c312a;
  }

  100% {
    background-color: transparent;
    color: black;
  }
}
</style>
